// Panel SPK Bordir Multi-Operator (ramah HP): formulir Bagi Pekerjaan satu langkah per layar, ringkasan tim + peringatan
// serah-terima hanya bila ada; rincian titik & tindakan jarang di menu "Detail".
// Aksi hanya untuk pemegang SPK utama (divalidasi backend); Owner/Admin hanya memantau.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Scissors, CheckCircle2, AlertTriangle, Check, Minus, Plus } from "lucide-react";
import { notify } from "./modal_center";
import { API, enc, HO_ST, post, run, TaskLine } from "./bordir_common";
import { SplitWizard, openBundles } from "./bordir_split_wizard";
import "./bordir_tasks.css";

const spkText = (b) => b.spk_points === null || b.spk_points === undefined ? "Jumlah titik tidak tercatat di SPK"
  : b.spk_points === 0 ? "SPK: tanpa bordir (0 titik)" : `SPK: ${b.spk_points} titik per pcs`;
const SRC = { spk: "sesuai SPK", spk_zero: "SPK 0 titik", operator_filled: "dilengkapi operator", operator_corrected: "dikoreksi operator" };

export function Stepper({ value, onChange, min = 0, max = 99, label, testid }) {
  const v = Number(value) || 0;
  return (
    <span className="bt-stepper" data-testid={testid}>
      <button type="button" aria-label={`Kurangi ${label}`} disabled={v <= min} onClick={() => onChange(Math.max(min, v - 1))} data-testid={`${testid}-minus`}><Minus size={16}/></button>
      <input type="number" inputMode="numeric" min={min} max={max} value={v} aria-label={label} data-testid={`${testid}-value`}
        onChange={e => onChange(Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10) || 0)))}/>
      <button type="button" aria-label={`Tambah ${label}`} disabled={v >= max} onClick={() => onChange(Math.min(max, v + 1))} data-testid={`${testid}-plus`}><Plus size={16}/></button>
    </span>
  );
}

function BundleRow({ d, b }) {
  return (
    <div className="bt-bundle-box" data-testid={`bordir-bundle-${d.product_id}-${b.bundle_id}`}>
      <div className="bt-bundle"><b>{b.label}</b> · {b.pcs} pcs · <span className="bt-muted">{spkText(b)}</span></div>
      {b.plan && <div className="bt-bundle">{b.positions.length ? b.positions.map(p => (
        <span key={p.pos_id} className={`bt-cap ${p.done === b.pcs ? "bt-cap-ok" : ""}`}>{p.label} · {p.points} titik · selesai {p.done}/{b.pcs}</span>))
        : <span className="bt-muted">tanpa titik bordir</span>}
        <span className="bt-muted">({SRC[b.plan.source] || b.plan.source}{b.plan.source_note ? `: ${b.plan.source_note}` : ""})</span></div>}
      {!!b.custody?.length && <div className="bt-muted">Barang dipegang: {b.custody.map(c => `${c.name} ${c.pcs}`).join(" · ")}
        {b.in_transit ? ` · sedang diserahkan ${b.in_transit}` : ""}{b.written_off ? ` · hilang/rusak ${b.written_off}` : ""}</div>}
      {!b.plan && <span className="bt-warn"><AlertTriangle size={12}/> Rincian titik belum dikonfirmasi</span>}
    </div>
  );
}

function AssignForm({ d, reload, rework = false }) {
  const planned = d.bundles.filter(b => b.plan && b.plan.positions.length);
  const [f, setF] = useState({ bundle_id: planned[0]?.bundle_id || "", operator_id: "", pcs: null, pos: [], rework_of: "", note: "" });
  const b = planned.find(x => x.bundle_id === f.bundle_id);
  if (!planned.length) return <span className="bt-muted">Konfirmasi Rincian Titik dulu.</span>;
  const left = (p) => rework ? b.pcs : b.pcs - p.assigned;
  const sel = b.positions.filter(p => f.pos.includes(p.pos_id));
  const max = sel.length ? Math.max(0, Math.min(...sel.map(left))) : 0;
  const pcs = Math.min(f.pcs ?? max, max);
  const tid = `${d.product_id}${rework ? "-rework" : ""}`;
  const src = d.tasks.filter(t => t.bundle_id === f.bundle_id && !t.is_rework && t.status !== "cancelled");
  const submit = () => run(() => post("/bordir-tasks", { order_number: d.order_number, product_id: d.product_id, bundle_id: f.bundle_id,
    operator_id: f.operator_id, positions: f.pos.map(pos_id => ({ pos_id })), pcs, is_rework: rework,
    rework_of: rework ? f.rework_of : "", note: f.note }), rework ? "Pekerjaan perbaikan ditugaskan" : "Pekerjaan dibagikan",
    async () => { setF({ ...f, pos: [], pcs: null, operator_id: "", note: "" }); await reload(); });
  return (
    <div className="bt-form-col" data-testid={`bordir-form-${tid}`}>
      {planned.length > 1 && <div className="bt-chips">{planned.map(x => (
        <button key={x.bundle_id} type="button" className={`bt-chip ${x.bundle_id === f.bundle_id ? "on" : ""}`} aria-pressed={x.bundle_id === f.bundle_id}
          onClick={() => setF({ ...f, bundle_id: x.bundle_id, pos: [], pcs: null })} data-testid={`bordir-form-bundle-${tid}-${x.bundle_id}`}>{x.label} · {x.pcs} pcs</button>))}</div>}
      <span className="bt-muted">Ketuk posisi yang dikerjakan:</span>
      <div className="bt-chips">{b.positions.map(p => {
        const on = f.pos.includes(p.pos_id); const out = left(p) <= 0;
        return (
          <button key={p.pos_id} type="button" className={`bt-chip bt-chip-pos ${on ? "on" : ""}`} disabled={out && !on} aria-pressed={on}
            onClick={() => setF(s => ({ ...s, pcs: null, pos: on ? s.pos.filter(x => x !== p.pos_id) : [...s.pos, p.pos_id] }))}
            data-testid={`bordir-form-pos-${tid}-${p.pos_id}`}>
            {on && <Check size={14}/>}<span><b>{p.label}</b><small>{p.points} titik · {out ? "sudah habis dibagi" : `sisa ${left(p)} pcs`}</small></span>
          </button>);
      })}</div>
      <select value={f.operator_id} onChange={e => setF({ ...f, operator_id: e.target.value })} className="bt-wide" data-testid={`bordir-form-operator-${tid}`}>
        <option value="">Pilih operator…</option>
        {d.operators.map(o => <option key={o.id} value={o.id}>{o.name}{o.id === d.me_id ? " (Saya)" : ""}</option>)}
      </select>
      {rework && <>
        <select value={f.rework_of} onChange={e => setF({ ...f, rework_of: e.target.value })} className="bt-wide" data-testid={`bordir-form-rework-of-${d.product_id}`}>
          <option value="">Perbaikan untuk tugas…</option>{src.map(t => <option key={t.id} value={t.id}>{t.operator_name} · {t.positions.map(p => p.label).join(", ")}</option>)}
        </select>
        <input type="text" className="bt-wide" placeholder="Catatan perbaikan" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid={`bordir-form-note-${d.product_id}`}/>
      </>}
      <div className="bt-row"><span>Jumlah pcs</span>
        <Stepper value={pcs} min={sel.length ? 1 : 0} max={max} label="pcs" testid={`bordir-form-pcs-${tid}`} onChange={n => setF({ ...f, pcs: n })}/>
        {sel.length > 0 && <span className="bt-muted">maks {max}</span>}</div>
      <button type="button" className="bt-btn bt-primary" disabled={!f.operator_id || !sel.length || !(pcs > 0) || (rework && (!f.rework_of || f.note.trim().length < 3))}
        onClick={submit} data-testid={`bordir-form-submit-${tid}`}>{rework ? "Tugaskan Perbaikan" : "Tugaskan"}</button>
    </div>
  );
}

function ResolveForm({ d, h, reload }) {
  const [f, setF] = useState({ kind: "found", pcs: h.diff - h.resolved_pcs, cause: "", action: "", to_operator_id: "", rework_task_id: "" });
  const reworks = d.tasks.filter(t => t.is_rework && t.bundle_id === h.bundle_id && t.status !== "cancelled");
  const left = h.diff - h.resolved_pcs;
  const submit = () => run(() => post(`/bordir-tasks/handover/${enc(h.id)}/resolve`, { ...f, pcs: Number(f.pcs) }), "Selisih dicatat", reload);
  return (
    <div className="bt-form-col bt-dispute" data-testid={`bordir-resolve-${h.id}`}>
      <span className="bt-warn">Kurang {left} pcs: {h.from_name} → {h.to_name} ({h.bundle_label}) · "{h.note_received}"</span>
      <select value={f.kind} className="bt-wide" onChange={e => setF({ ...f, kind: e.target.value })} data-testid={`bordir-resolve-kind-${h.id}`}>
        {Object.entries(d.resolve_kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="bt-row"><span>Jumlah pcs</span><Stepper value={f.pcs} min={1} max={left} label="pcs selisih" testid={`bordir-resolve-pcs-${h.id}`} onChange={n => setF({ ...f, pcs: n })}/></div>
      {f.kind === "found" && <select value={f.to_operator_id} className="bt-wide" onChange={e => setF({ ...f, to_operator_id: e.target.value })} data-testid={`bordir-resolve-to-${h.id}`}>
        <option value="">Barang sekarang dipegang…</option>{d.operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}
      {f.kind === "rework" && <select value={f.rework_task_id} className="bt-wide" onChange={e => setF({ ...f, rework_task_id: e.target.value })} data-testid={`bordir-resolve-rework-${h.id}`}>
        <option value="">{reworks.length ? "Pilih pekerjaan perbaikan…" : "Buat perbaikan dulu di Tindakan Lainnya"}</option>
        {reworks.map(t => <option key={t.id} value={t.id}>{t.operator_name} · {t.pcs_assigned} pcs</option>)}</select>}
      <input type="text" className="bt-wide" placeholder="Penyebab" value={f.cause} onChange={e => setF({ ...f, cause: e.target.value })} data-testid={`bordir-resolve-cause-${h.id}`}/>
      <input type="text" className="bt-wide" placeholder="Tindakan penyelesaian" value={f.action} onChange={e => setF({ ...f, action: e.target.value })} data-testid={`bordir-resolve-action-${h.id}`}/>
      <button type="button" className="bt-btn bt-primary" onClick={submit} data-testid={`bordir-resolve-submit-${h.id}`}
        disabled={f.cause.trim().length < 3 || f.action.trim().length < 3 || (f.kind === "found" && !f.to_operator_id) || (f.kind === "rework" && !f.rework_task_id)}>Simpan Penyelesaian</button>
    </div>
  );
}

export function ReadinessList({ r, testid }) {
  if (!r?.multi || r.ready) return null;
  return <ul className="bt-reasons" data-testid={testid}>{r.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

function FinishStep({ d, manage, reload }) {
  const r = d.readiness;
  return (
    <div className="bt-form-col" data-testid={`bordir-readiness-${d.product_id}`}>
      {r.ready ? <span className="bt-total bt-total-ok"><CheckCircle2 size={14}/> Siap. Tekan <b>Selesai Tahap Bordir</b> di kartu SPK. Diperiksa {r.final_check?.by?.name}.</span>
        : <><span className="bt-warn">Belum bisa Selesai Tahap Bordir:</span><ReadinessList r={r} testid={`bordir-reasons-${d.product_id}`}/></>}
      {manage && r.ok_except_final && !r.final_check && <button type="button" className="bt-btn bt-primary" data-testid={`bordir-final-check-${d.product_id}`}
        onClick={() => run(() => post("/bordir-tasks/final-check", { order_number: d.order_number, product_id: d.product_id }), "Pemeriksaan akhir dicatat", reload)}>
        Saya sudah periksa hasil akhir</button>}
    </div>
  );
}

function TaskMore({ d, t, reload }) {
  const [to, setTo] = useState("");
  const open = ["assigned", "in_progress"].includes(t.status);
  const cancel = async () => {
    const reason = await notify.prompt({ title: "Batalkan tugas ini?", message: `${t.operator_name} · ${t.bundle_label}`, required: true, minLength: 3,
      primaryLabel: "Batalkan", secondaryLabel: "Kembali", danger: true });
    if (reason) run(() => post(`/bordir-tasks/${enc(t.id)}/cancel`, null, { reason }), "Tugas dibatalkan", reload);
  };
  const move = async () => {
    const reason = await notify.prompt({ title: `Alihkan sisa ${t.pcs_assigned - t.pcs_done} pcs?`, message: `Hasil ${t.pcs_done} pcs tetap atas nama ${t.operator_name}. Barang tetap harus diserahkan secara fisik.`,
      required: true, minLength: 3, primaryLabel: "Alihkan", secondaryLabel: "Batal" });
    if (reason) run(() => post(`/bordir-tasks/${enc(t.id)}/reassign-remaining`, { to_operator_id: to, reason }), "Sisa tugas dialihkan", reload);
  };
  if (!open) return null;
  return (
    <div className="bt-task" data-testid={`bordir-task-more-${t.id}`}>
      <span><b>{t.operator_name}</b> · {t.positions.map(p => p.label).join(", ")} · {t.pcs_done}/{t.pcs_assigned} pcs</span>
      {t.pcs_done === 0 && <button type="button" className="bt-link" onClick={cancel} data-testid={`bordir-task-cancel-${t.id}`}>Batalkan</button>}
      {!t.is_rework && t.pcs_done < t.pcs_assigned && <span className="bt-ho">
        <select value={to} onChange={e => setTo(e.target.value)} data-testid={`bordir-reassign-to-${t.id}`}>
          <option value="">Alihkan sisa ke…</option>{d.operators.filter(o => o.id !== t.operator_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button type="button" className="bt-btn" disabled={!to} onClick={move} data-testid={`bordir-reassign-submit-${t.id}`}>Alihkan sisa</button></span>}
    </div>
  );
}

function TransferBox({ d, reload }) {
  const [to, setTo] = useState("");
  const tr = d.transfer;
  if (tr?.status === "pending") return (
    <div className="bt-muted" data-testid={`bordir-transfer-pending-${d.product_id}`}>Menunggu <b>{tr.to_name}</b> menyetujui pengalihan penanggung jawab ·{" "}
      <button type="button" className="bt-link" onClick={() => run(() => post(`/bordir-tasks/transfer/${enc(d.order_number)}/${enc(d.product_id)}/cancel`), "Pengajuan dibatalkan", reload)}
        data-testid={`bordir-transfer-cancel-${d.product_id}`}>Batalkan</button></div>);
  const ask = async () => {
    const note = await notify.prompt({ title: "Alihkan penanggung jawab SPK?", message: "Pindah setelah penerima menyetujui.",
      required: true, minLength: 3, primaryLabel: "Ajukan", secondaryLabel: "Batal" });
    if (note) run(() => post("/bordir-tasks/transfer", { order_number: d.order_number, product_id: d.product_id, to_operator_id: to, note }), "Pengajuan dikirim", reload);
  };
  return (
    <div className="bt-form">
      <select value={to} onChange={e => setTo(e.target.value)} data-testid={`bordir-transfer-to-${d.product_id}`}>
        <option value="">Alihkan penanggung jawab ke…</option>{d.operators.filter(o => o.id !== d.me_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <button type="button" className="bt-btn" disabled={!to} onClick={ask} data-testid={`bordir-transfer-submit-${d.product_id}`}>Ajukan</button>
    </div>
  );
}

function ItemPanel({ orderNumber, productId, allowManage = false, onChanged }) {
  const [d, setD] = useState(null);
  const [formOpen, setFormOpen] = useState(true);
  const [showTeam, setShowTeam] = useState(null);
  const [lastSplit, setLastSplit] = useState("");
  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/bordir-tasks/by-item/${enc(orderNumber)}/${enc(productId)}`, { withCredentials: true })).data); }
    catch { setD(false); }
    if (onChanged) onChanged();
  }, [orderNumber, productId, onChanged]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const manage = allowManage && d.can_manage;
  const tasks = d.tasks.filter(t => t.status !== "cancelled");
  const openHo = d.handovers.filter(h => h.status === "pending" || h.status === "disputed");
  const allSplit = !openBundles(d).length;
  const nDone = tasks.filter(t => t.status === "done").length;
  const pcsDone = tasks.reduce((a, t) => a + (t.pcs_done || 0), 0), pcsAll = tasks.reduce((a, t) => a + (t.pcs_assigned || 0), 0);
  const team = showTeam ?? !manage;
  return (
    <div className="bt-item" data-testid={`bordir-item-${productId}`}>
      <div className="bt-head"><Scissors size={14}/><strong>{d.product_name}</strong><span className="bt-muted">Penanggung jawab: {d.main_pic || "—"}</span></div>
      {allowManage && !d.can_manage && <span className="bt-muted" data-testid={`bordir-form-locked-${productId}`}>Hanya pemegang SPK yang dapat membagi pekerjaan.</span>}
      {d.pcs_unknown && <div className="bw-why" role="alert" data-testid={`bordir-pcs-unknown-${productId}`}>
        <b>Jumlah barang belum terbaca — hubungi penanggung jawab.</b>{d.pcs_reason ? ` (${d.pcs_reason})` : ""} Pekerjaan belum bisa dibagi.</div>}
      {lastSplit && <span className="bt-total bt-total-ok" data-testid={`bordir-split-done-${productId}`}><CheckCircle2 size={14}/> Berhasil dibagi: {lastSplit}</span>}
      {manage && !d.pcs_unknown && (allSplit ? <span className="bt-total bt-total-ok" data-testid={`bordir-all-split-${productId}`}><CheckCircle2 size={14}/> Semua bagian sudah dibagi.</span>
        : formOpen ? <SplitWizard d={d} reload={load} onDone={(txt) => { setLastSplit(txt); setFormOpen(false); }}/>
        : <button type="button" className="bt-btn bt-primary" onClick={() => { setLastSplit(""); setFormOpen(true); }} data-testid={`bordir-split-more-${productId}`}>Bagi sisa pekerjaan</button>)}
      {tasks.length > 0 && <div className="bt-team-sum" data-testid={`bordir-team-sum-${productId}`}>
        <span>Tim: <b>{tasks.length} tugas</b> · {nDone} selesai · {pcsDone}/{pcsAll} pcs dikerjakan</span>
        <button type="button" className="bt-link" onClick={() => setShowTeam(!team)} aria-expanded={team} data-testid={`bordir-team-toggle-${productId}`}>
          {team ? "Sembunyikan" : "Lihat Progres Tim"}</button></div>}
      {team && tasks.map(t => <div key={t.id} className="bt-task" data-testid={`bordir-task-${t.id}`}><TaskLine t={t}/>
        {t.reassigned_pcs ? <span className="bt-muted">· sisa {t.reassigned_pcs} pcs dialihkan ke {t.reassigned_to}</span> : null}</div>)}
      {openHo.length > 0 && <div className="bt-ho-alert" role="alert" data-testid={`bordir-ho-alert-${productId}`}>
        <b><AlertTriangle size={14}/> Serah-terima perlu ditindaklanjuti ({openHo.length})</b>
        {openHo.map(h => manage && h.status === "disputed" ? <ResolveForm key={h.id} d={d} h={h} reload={load}/> : (
          <div key={h.id} className="bt-task bt-incoming" data-testid={`bordir-item-handover-${h.id}`}>
            {h.from_name} → {h.to_name} · {h.pcs_sent} pcs ({h.bundle_label}) · {HO_ST[h.status]}</div>))}</div>}
      {d.readiness?.multi && tasks.length > 0 && <FinishStep d={d} manage={manage} reload={load}/>}
      {!d.pcs_unknown && <details className="bt-more" data-testid={`bordir-detail-${productId}`}>
        <summary>Detail (rincian titik{manage ? " & tindakan lainnya" : ""})</summary>
        <strong className="bt-sub">Rincian titik per variasi</strong>
        {d.bundles.map(b => <BundleRow key={b.bundle_id} d={d} b={b}/>)}
        {manage && <div data-testid={`bordir-more-${productId}`} className="bt-form-col">
          <strong className="bt-sub">Tindakan Lainnya</strong>
          {tasks.map(t => <TaskMore key={t.id} d={d} t={t} reload={load}/>)}
          <strong className="bt-sub">Pekerjaan perbaikan</strong>
          <AssignForm d={d} reload={load} rework/>
          <strong className="bt-sub">Alihkan penanggung jawab SPK</strong>
          <TransferBox d={d} reload={load}/>
        </div>}
      </details>}
    </div>
  );
}

export function BordirTasksPanel({ order }) {
  const items = (order.products || []).filter(p => /bordir/i.test(p.production_route || ""));
  if (String(order.source || "").toLowerCase() === "shopee" || !items.length || ["Draft", "Dibatalkan"].includes(order.order_status)) return null;
  return (
    <section className="bt-panel" data-testid="bordir-tasks-panel">
      <div className="bt-head"><Scissors size={16}/><strong>Pemantauan Pekerjaan Bordir</strong>
        <span className="bt-muted">Hanya dipantau. Pembagian dilakukan pemegang SPK di dashboard Operator Bordir.</span></div>
      {items.map(p => <ItemPanel key={p.id} orderNumber={order.order_number} productId={p.id}/>)}
    </section>
  );
}

export function BordirSplitPanel({ orderNumber, productId, onChanged }) {
  return (
    <section className="bt-panel" data-testid={`bordir-split-panel-${productId}`}>
      <ItemPanel orderNumber={orderNumber} productId={productId} allowManage onChanged={onChanged}/>
    </section>
  );
}

export function useBordirReadiness(items, enabled) {
  const [map, setMap] = useState({});
  const key = items.map(x => `${x.order_number}::${x.product_id}`).join("|");
  const refresh = useCallback(async () => {
    if (!enabled) return;
    const out = {};
    await Promise.all(key.split("|").filter(Boolean).map(async k => {
      const [o, p] = k.split("::");
      try { out[k] = (await axios.get(`${API}/bordir-tasks/readiness/${enc(o)}/${enc(p)}`, { withCredentials: true })).data; } catch { /* abaikan */ }
    }));
    setMap(out);
  }, [key, enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return [map, refresh];
}
